import { X } from "lucide-react";
import PropTypes from "prop-types";
import { useState } from "react";

const integrationSteps = [
    "Get your API key from your settings page.",
    "Include the API key in the Authorization header for every request as Bearer <YOUR_API_KEY>.",
    "Call the drugs endpoint and store the returned drugId, doctor_amount, patient_amount, and dos values.",
    "Call the SIG endpoint if you want to populate a directions picker in your clinic app.",
    "For patient orders, choose a daysOfSupply value from the selected drug's dos array.",
    "Send direction for every medication item. Use a SIG from the SIG endpoint or your own validated directions text.",
    "Call the create order endpoint with a top-level patients array. One order is created for each patient item.",
    "Use the returned orderIds array and the orders endpoint to track submitted orders.",
    "Set a valid HTTPS webhook URL in clinic settings if you want real-time VeRx order updates.",
    "Set a shared webhook secret in clinic settings and use it to verify webhook signatures on your server.",
    "Handle webhook events idempotently using the top-level event id because webhook delivery can be retried.",
];

const orderRules = [
    "Every create-order request must include a top-level patients array.",
    "Each patients[i] item creates one order in the pharmacy system.",
    "Every medication item must include direction.",
    "Patient orders must send daysOfSupply for each medication item. The backend uses the matching dos amount for pricing.",
    "Bulk orders use doctor_amount from the drugs endpoint for pricing.",
    "Do not send pricing fields such as amount, price, doctor_amount, patient_amount, doctorAmount, or patientAmount when creating orders. The API rejects them.",
    "The API recalculates medication pricing on the server from the selected drug and order method.",
    "The drugs endpoint exposes pricing metadata for display and selection only. It is not a create-order payload contract.",
];

const topLevelOrderFields = [
    { name: "doctorFirstname", type: "string", required: true, note: "Ordering doctor first name." },
    { name: "doctorLastname", type: "string", required: true, note: "Ordering doctor last name." },
    { name: "doctorPrefix", type: "string", required: true, note: "Example: Dr." },
    { name: "doctorNPI", type: "string", required: true, note: "Doctor NPI Number" },
    { name: "doctorDEA", type: "string", required: false, note: "Doctor DEA Number" },
    { name: "doctorLicense", type: "string", required: true, note: "Doctor License" },
    { name: "doctorGender", type: "string", required: true, note: "Doctor gender." },
    { name: "doctorAddress1", type: "string", required: true, note: "Doctor primary address." },
    { name: "doctorHomePhone", type: "string", required: true, note: "Doctor home phone in a valid international format." },
    { name: "doctorWorkPhone", type: "string", required: true, note: "Doctor work phone in a valid international format." },
    { name: "doctorCountry", type: "string", required: true, note: "Doctor country name." },
    { name: "doctorState", type: "string", required: true, note: "Doctor state." },
    { name: "doctorCity", type: "string", required: true, note: "Doctor city." },
    { name: "doctorZipcode", type: "string", required: true, note: "Doctor zipcode." },
    { name: "doctorSignature", type: "string", required: true, note: "Text or URL representing the doctor's signature." },
    { name: "doctorEmail", type: "string", required: true, note: "Valid doctor email address." },
    { name: "orderType", type: "string", required: true, note: "Accepted values: New, Refill." },
    { name: "deliveryType", type: "string", required: true, note: "Allowed values: Deliver to Patient, Deliver to Doctor." },
    { name: "orderMethod", type: "string", required: true, note: "Accepted values: Patient, Bulk." },
    { name: "orderBillingPreference", type: "string", required: true, note: "Allowed values: Bill Facility, Bill Patient." },
    { name: "patients", type: "array", required: true, note: "One order is created for each patient object in the array." },
    { name: "orderNote", type: "string", required: false, note: "Optional note attached to all orders created in the request." },
];

const patientObjectFields = [
    { name: "patientFirstname", type: "string", required: true, note: "Patient first name." },
    { name: "patientLastname", type: "string", required: true, note: "Patient last name." },
    { name: "patientDob", type: "string", required: true, note: "Format: YYYY-MM-DD." },
    { name: "patientBirthSex", type: "string", required: true, note: "Patient birth sex." },
    { name: "patientAddress1", type: "string", required: true, note: "Patient primary address." },
    { name: "patientCountry", type: "string", required: true, note: "Country name used for phone validation." },
    { name: "patientState", type: "string", required: true, note: "Patient state." },
    { name: "patientCity", type: "string", required: true, note: "Patient city." },
    { name: "patientZipcode", type: "string", required: true, note: "Patient zipcode." },
    { name: "patientAllergies", type: "string", required: false, note: "Patient allergies." },
    { name: "patientDiseases", type: "string", required: false, note: "Patient diseases." },
    { name: "patientCellPhone", type: "string", required: true, note: "Valid phone number for the patient country." },
    { name: "patientEmail", type: "string", required: true, note: "Valid patient email address." },
    { name: "deliveryAddress", type: "string", required: true, note: "Delivery address for that patient's order." },
    { name: "deliveryCity", type: "string", required: true, note: "Delivery city." },
    { name: "deliveryState", type: "string", required: true, note: "Delivery state." },
    { name: "deliveryZipCode", type: "string", required: true, note: "Delivery zipcode." },
    { name: "deliveryPhone", type: "string", required: true, note: "Delivery contact phone." },
    { name: "shippingService", type: "object", required: true, note: "Use an object returned from the shipping services endpoint." },
    { name: "medication", type: "array", required: true, note: "Patient-specific medication items. Use patient or bulk schema depending on orderMethod." },
];

const patientMedicationFields = [
    { name: "name", type: "string", required: true, note: "Drug display name from the drugs endpoint." },
    { name: "drugId", type: "string", required: true, note: "Use the drugId returned from the drugs endpoint." },
    { name: "quantity", type: "string | number", required: true, note: "Medication quantity for this order." },
    { name: "direction", type: "string", required: true, note: "SIG or directions text for this medication." },
    { name: "daysOfSupply", type: "string | number", required: true, note: "Required for patient orders. Must match one of the drug's dos.number_of_days values." },
    { name: "reasonForCompounding", type: "string", required: true, note: "Required for patient orders." },
];

const bulkMedicationFields = [
    { name: "name", type: "string", required: true, note: "Drug display name from the drugs endpoint." },
    { name: "drugId", type: "string", required: true, note: "Use the drugId returned from the drugs endpoint." },
    { name: "quantity", type: "string | number", required: true, note: "Medication quantity for this bulk order." },
    { name: "direction", type: "string", required: true, note: "SIG or directions text for this medication." },
];

const shippingServiceFields = [
    { name: "name", type: "string", required: true, note: "Shipping service label." },
    { name: "type", type: "string", required: true, note: "Example: pickup, delivery." },
    { name: "service_level", type: "string", required: true, note: "Human-readable service level." },
    { name: "state", type: "string", required: true, note: "State for which the service applies." },
    { name: "price", type: "string | number", required: true, note: "Returned by the shipping service endpoint." },
    { name: "id", type: "string", required: true, note: "Service identifier." },
    { name: "weight", type: "string", required: false, note: "Optional weight band." },
    { name: "carrier", type: "string", required: false, note: "Optional carrier display name." },
];

const getStatesResponse = `[
  {
    "code2": "US",
    "code3": "USA",
    "name": "United States",
    "states": [
      { "code": "TX", "name": "Texas", "subdivision": "state" },
      { "code": "CA", "name": "California", "subdivision": "state" },
      { "code": "FL", "name": "Florida", "subdivision": "state" }
    ]
  }
]`;

const getShippingServicesResponse = `[
  {
    "name": "Pick Up",
    "type": "pickup",
    "service_level": "Pick Up from Pharmacy",
    "state": "Texas",
    "price": 0,
    "id": "pickup",
    "weight": ">1",
    "carrier": "Pick Up"
  },
  {
    "id": "83019274",
    "name": "FEDEX",
    "type": "delivery",
    "service_level": "Any of the 50 states",
    "state": "any",
    "price": "70",
    "weight": "5",
    "carrier": "FEDEX"
  }
]`;

const getDrugsResponse = `[
  {
    "name": "Glutathione",
    "c2": "",
    "category": "",
    "device": "Vial",
    "form": "",
    "id": "540624387",
    "mg": "200mg/mL",
    "route_of_admin": "IM",
    "size": "10mL",
    "strength": "",
    "drugId": "152117761",
    "type": "vial",
    "doctor_amount": "100",
    "patient_amount": "",
    "dos": [
      { "number_of_days": "30", "amount": "200" },
      { "number_of_days": "45", "amount": "400" }
    ]
  }
]`;

const getSigResponse = `[
  "Inject 0.10ml (10 units) subq once weekly. (0.25mg)",
  "Inject 0.20ml (20 units) subq once weekly. (0.5mg)",
  "Inject 0.30ml (30 units) subq once weekly. (0.75mg)",
  "Inject 0.40ml (40 units) subq once weekly. (1.0mg)"
]`;

const reasonsResponse = `[
    "Allergy to phenol (preservative in commercial GLP-1)",
    "Allergy or sensitivity to propylene glycol in commercial injection",
    "Allergy/intolerance to multiple excipients in all commercial GLP-1 products",
    "Severe injection-site reactions with commercial product",
    "Intolerable injection pain due to pH or preservatives in commercial drug",
    "Ultra-low starting dose required (commercial dose too high)",
    "Intermediate dosage strength needed (e.g., 0.75 mg not commercially available)",
    "Requires gradual titration with smaller increments than available commercial doses",
    "Higher dose required for efficacy beyond commercial max dose (e.g., >2.4 mg/week)",
    "Needle phobia requiring a non-injectable formulation",
    "Cannot swallow oral tablet and injections are contraindicated",
    "Bleeding disorder (anticoagulation) - non-injectable form required",
    "Severe dexterity/visual impairment - cannot use pen injectors",
    "Pediatric patient requires custom low-dose or flavored formulation",
    "Daily injection intolerable - needs extended dosing interval formulation",
    "Requires altered drug release profile for side-effect management",
    "Requires desensitization protocol with micro-dosed increments",
    "Combination therapy medically necessary (e.g., GLP-1 + B12 in single injection)",
    "All approved GLP-1 options failed (last resort compounding needed)",
    "Contraindication to a specific commercial formulation component"
]`

const getOrdersResponse = `[
  {
    "id": "194786389",
    "patient_id": "957684892",
    "doctor_id": "332874886",
    "order_type": "New",
    "delivery_type": "Deliver to Patient",
    "delivery_address": "2100 West Loop South",
    "delivery_phone": "+17135550155",
    "pharmacy_id": "01J4Q8BXQMBSFKXRSS8TM1R2VG",
    "medication": "[{\\"name\\":\\"Glutathione\\",\\"amount\\":20,\\"device\\":\\"Vial\\",\\"id\\":\\"135607685\\",\\"mg\\":\\"200mg/mL\\",\\"quantity\\":\\"1\\",\\"route_of_admin\\":\\"IM\\",\\"size\\":\\"10mL\\",\\"drugId\\":\\"152117761\\",\\"type\\":\\"vial\\",\\"reasonForCompounding\\":\\"For health purposes\\",\\"daysOfSupply\\":\\"60\\"}]",
    "payment_status": "paid",
    "order_status": "processed",
    "order_method": "Patient",
    "order_billing_preference": "Bill Patient",
    "delivery_service": "pickup",
    "shipping_service": "{\\"name\\":\\"Pick Up\\",\\"id\\":\\"pickup\\",\\"price\\":0,\\"weight\\":\\">1\\",\\"carrier\\":\\"Pick Up\\",\\"service_level\\":\\"Pick Up from Pharmacy\\",\\"type\\":\\"pickup\\",\\"state\\":\\"Texas\\"}",
    "order_source": "VeRx",
    "delivery_state": "Texas",
    "delivery_zipcode": "77027",
    "delivery_city": "Houston",
    "step": "pv2",
    "is_fulfilled": "no",
    "first_processed_at": "2026-04-05T16:25:49.000Z",
    "doctor": {
      "id": "332874886",
      "firstname": "John",
      "lastname": "Doe",
      "email": "doctoremail@test.com"
    },
    "patient": {
      "id": "957684892",
      "firstname": "Maria",
      "lastname": "Johnson",
      "email_address": "patientemail@test.com"
    },
    "pharmacy": {
      "id": "01J4Q8BXQMBSFKXRSS8TM1R2VG",
      "pharmacy_name": "Neatdwell"
    },
    "facility": {
      "id": "01J4Q8HHYBCKVGVA8V066QMQBN",
      "name": "dWELCOR "
    },
    "steps": [
      { "step": "Intake", "is_done": true, "is_active": false },
      { "step": "PV1", "is_done": true, "is_active": false },
      { "step": "Fulfillment", "is_done": false, "is_active": false },
      { "step": "PV2", "is_done": true, "is_active": true },
      { "step": "Shipping", "is_done": false, "is_active": false }
    ]
  }
]`;

const cancelOrderResponse = `{
    "message": "Order #674327278 canceled"
}`

const getSingleOrderResponse = `{
  "id": "674327278",
  "patient_id": "",
  "doctor_id": "332874886",
  "order_type": "New",
  "delivery_type": "Deliver to Doctor",
  "delivery_address": "123 Main St",
  "delivery_phone": "+17133609788",
  "pharmacy_id": "01J4Q8BXQMBSFKXRSS8TM1R2VG",
  "medication": "[{\\"name\\":\\"Glutathione\\",\\"amount\\":210,\\"device\\":\\"Vial\\",\\"id\\":\\"336927932\\",\\"mg\\":\\"200mg/mL\\",\\"quantity\\":\\"3\\",\\"route_of_admin\\":\\"IM\\",\\"size\\":\\"10mL\\",\\"drugId\\":\\"152117761\\",\\"type\\":\\"vial\\",\\"reasonForCompounding\\":\\"\\"}]",
  "payment_status": "paid",
  "order_status": "processing",
  "order_method": "Bulk",
  "order_billing_preference": "Bill Facility",
  "order_source": "VeRx",
  "delivery_service": "pickup",
  "shipping_service": "{\\"name\\":\\"Pick Up\\",\\"id\\":\\"pickup\\",\\"price\\":0,\\"weight\\":\\">1\\",\\"carrier\\":\\"Pick Up\\",\\"service_level\\":\\"Pick Up from Pharmacy\\",\\"type\\":\\"pickup\\",\\"state\\":\\"Texas\\"}",
  "delivery_state": "Texas",
  "delivery_zipcode": "77080",
  "delivery_city": "Houston",
  "step": "",
  "is_fulfilled": "no",
  "tracking_id": "",
  "doctor": {
    "id": "332874886",
    "firstname": "John",
    "lastname": "Doe",
    "email": "doctoremail@test.com"
  },
  "pharmacy": {
    "id": "01J4Q8BXQMBSFKXRSS8TM1R2VG",
    "pharmacy_name": "Neatdwell"
  },
  "facility": {
    "id": "01J4Q8HHYBCKVGVA8V066QMQBN",
    "name": "dWELCOR "
  },
  "patient": null,
  "shipping": null,
  "steps": [
    { "step": "Intake", "is_done": false, "is_active": false },
    { "step": "PV1", "is_done": false, "is_active": false },
    { "step": "Fulfillment", "is_done": false, "is_active": false },
    { "step": "PV2", "is_done": false, "is_active": false },
    { "step": "Shipping", "is_done": false, "is_active": false }
  ]
}`;

const webhookEventResponse = `{
  "id": "01KSSMMDXQM182KXA92MJGDYR1",
  "event": "order.accepted",
  "created_at": "2026-05-29T10:31:44.311Z",
  "data": {
    "order": {
      "id": "748995861",
      "patient_id": "210303028",
      "doctor_id": "332874886",
      "order_type": "New",
      "delivery_type": "Deliver to Patient",
      "delivery_address": "600 Montgomery St",
      "delivery_phone": "+14155550112",
      "pharmacy_id": "01J4Q8BXQMBSFKXRSS8TM1R2VG",
      "medication": "[{\\"name\\":\\"Glutathione\\",\\"amount\\":20,\\"c2\\":\\"\\",\\"category\\":\\"\\",\\"device\\":\\"Vial\\",\\"direction\\":\\"Inject 0.20ml (20 units) subq once weekly. (0.5mg)\\",\\"form\\":\\"\\",\\"id\\":\\"791910842\\",\\"mg\\":\\"200mg/mL\\",\\"quantity\\":\\"1\\",\\"route_of_admin\\":\\"IM\\",\\"size\\":\\"10mL\\",\\"strength\\":\\"\\",\\"drugId\\":\\"152117761\\",\\"type\\":\\"vial\\",\\"reasonForCompounding\\":\\"For health purposes\\",\\"daysOfSupply\\":\\"60\\"}]",
      "pharmacy_note": "",
      "order_note": "",
      "payment_status": "paid",
      "order_status": "accepted",
      "order_method": "Patient",
      "created_at": "2026-05-29T10:30:26.000Z",
      "shipped_at": "",
      "completed_at": "",
      "updated_at": null,
      "facility_id": "01J4Q8HHYBCKVGVA8V066QMQBN",
      "updated": "",
      "filepath": "",
      "rejection_note": "",
      "priority": "",
      "supervising_practitioner_id": "332874886",
      "shipping_id": "",
      "invoices_generated": "",
      "amount_paid": "",
      "invoice_url": "",
      "profit_claimed": "",
      "amount": "",
      "rep_id": "",
      "client_id": "",
      "tracking_id": "",
      "order_billing_preference": "Bill Patient",
      "pharmacy_ids": "[\\"01J4Q8BXQMBSFKXRSS8TM1R2VG\\"]",
      "delivery_service": "",
      "invoice_id": "",
      "is_case": "",
      "case_ids": "",
      "delivery_patient_id": "",
      "payout_status": "unpaid",
      "order_source": "VeRx",
      "pharmacy_practice_id": "",
      "pharmacy_admin_id": "",
      "patient_online_status": "offline",
      "prescriber_online_status": "offline",
      "platform_fee": "",
      "platform_fee_status": "",
      "surescript_msg_id": "",
      "prescriber_order_num": "",
      "allergy_xml": null,
      "patient_xml": null,
      "pharmacy_xml": null,
      "prescriber_xml": null,
      "medication_prescribed_xml": null,
      "prescriber_sender_id": "",
      "rx_request_status": "",
      "rx_request_at": null,
      "rx_renewal_note": null,
      "rx_renewal_status": "",
      "deleted_at": null,
      "rx_renewal_request_at": null,
      "rx_renewal_at": null,
      "prev_order_id": "",
      "next_order_id": "",
      "rx_fill_indicator": "false",
      "xml": null,
      "fill_indicator_count": "0",
      "prohibit_renewal_request": "false",
      "shipping_service": "{\\"name\\":\\"All states\\",\\"id\\":\\"860138206\\",\\"price\\":\\"70\\",\\"weight\\":\\"5\\",\\"carrier\\":\\"FedEx\\",\\"service_level\\":\\"Any of the 50 states\\",\\"type\\":\\"FIRST_OVERNIGHT\\",\\"state\\":\\"any\\"}",
      "delivery_state": "California",
      "delivery_zipcode": "94111",
      "delivery_city": "San Francisco",
      "med_type": "single",
      "created_by": "332874886",
      "house_account_id": "337983578",
      "mx_invoice_id": "",
      "mx_receipt_id": "",
      "last_generated_rx_ids": null,
      "invoice_status": "unpaid",
      "signature": null,
      "step": "intake",
      "is_fulfilled": "no",
      "delivery_address_verified": "false",
      "first_processed_at": null,
      "canceled_at": null,
      "seq": 1035,
      "doctor": {
        "id": "332874886",
        "firstname": "John",
        "lastname": "Doe"
      },
      "pharmacy": {
        "id": "01J4Q8BXQMBSFKXRSS8TM1R2VG",
        "pharmacy_name": "Neatdwell"
      },
      "facility": {
        "id": "01J4Q8HHYBCKVGVA8V066QMQBN",
        "name": "dWELCOR "
      },
      "shipping": null,
      "steps": [
        { "step": "Intake", "is_done": true, "is_active": true },
        { "step": "PV1", "is_done": false, "is_active": false },
        { "step": "Fulfillment", "is_done": false, "is_active": false },
        { "step": "PV2", "is_done": false, "is_active": false },
        { "step": "Shipping", "is_done": false, "is_active": false }
      ]
    },
    "trigger": {
      "step": "Intake",
      "status": "accepted"
    }
  }
}`;

const webhookHeadersExample = `X-BlendCare-Event-Id: 01KSSMMDXQM182KXA92MJGDYR1
X-BlendCare-Event: order.accepted
X-BlendCare-Timestamp: 2026-05-29T10:31:44.311Z
X-BlendCare-Signature: sha256=<hex_hmac_signature>`;

const webhookVerificationExample = `const crypto = require("crypto");

const timestamp = req.header("X-BlendCare-Timestamp");
const signature = req.header("X-BlendCare-Signature");
const rawBody = req.rawBody; // exact JSON string received

const expected = "sha256=" + crypto
  .createHmac("sha256", process.env.BLENDCARE_WEBHOOK_SECRET)
  .update(\`\${timestamp}.\${rawBody}\`)
  .digest("hex");

if (signature !== expected) {
  throw new Error("Invalid webhook signature");
}`;

const webhookVerificationPythonExample = `import hmac
import hashlib

timestamp = request.headers.get("X-BlendCare-Timestamp", "")
signature = request.headers.get("X-BlendCare-Signature", "")
raw_body = request.get_data(as_text=True)  # exact JSON string received
secret = os.environ["BLENDCARE_WEBHOOK_SECRET"]

payload_to_sign = f"{timestamp}.{raw_body}"
expected = "sha256=" + hmac.new(
    secret.encode("utf-8"),
    payload_to_sign.encode("utf-8"),
    hashlib.sha256,
).hexdigest()

if not hmac.compare_digest(signature, expected):
    raise ValueError("Invalid webhook signature")`;

const createPatientOrderRequest = `{
  "doctorFirstname": "John",
  "doctorLastname": "Doe",
  "doctorPrefix": "Dr.",
  "doctorGender": "Male",
  "doctorAddress1": "123 Main St",
  "doctorNPI": "123456789X",
  "doctorDEA": "AB1234563",
  "doctorLicense": "MDCN/R/68971",
  "doctorHomePhone": "+13109180629",
  "doctorWorkPhone": "+17133609788",
  "doctorCountry": "United States",
  "doctorState": "Texas",
  "doctorCity": "Houston",
  "doctorZipcode": "77080",
  "doctorSignature": "Signed electronically",
  "doctorEmail": "doctoremail@test.com",
  "orderType": "New",
  "deliveryType": "Deliver to Patient",
  "orderMethod": "Patient",
  "orderBillingPreference": "Bill Patient",
  "patients": [
    {
      "patientFirstname": "Janet",
      "patientLastname": "Walker",
      "patientDob": "1991-04-15",
      "patientBirthSex": "Female",
      "patientAddress1": "9845 Mocking Bird Hill Lane",
      "patientCountry": "United States",
      "patientState": "Texas",
      "patientCity": "Houston",
      "patientZipcode": "77080",
      "patientAllergies": "CRESTOR, SULFA",
      "patientDiseases": "OSA, OA, NASH",
      "patientCellPhone": "+13109180629",
      "patientEmail": "patientemail@test.com",
      "deliveryAddress": "9845 Mocking Bird Hill Lane",
      "deliveryCity": "Houston",
      "deliveryState": "Texas",
      "deliveryZipCode": "77080",
      "deliveryPhone": "+13109180629",
      "shippingService": {
        "name": "Pick Up",
        "type": "pickup",
        "service_level": "Pick Up from Pharmacy",
        "state": "Texas",
        "price": "0",
        "id": "pickup",
        "weight": ">1",
        "carrier": "Pick Up"
      },
      "medication": [
        {
          "name": "Glutathione",
          "drugId": "152117761",
          "quantity": "1",
          "daysOfSupply": "30",
          "directions": [
                "month": 1,
                "label": "1st month sig",
                "text": "Inject 0.10ml (10 units) subq once weekly. (0.25mg)"
          ]
          "reasonForCompounding": "For health purposes"
        }
      ]
    },
    {
      "patientFirstname": "Maria",
      "patientLastname": "Johnson",
      "patientDob": "1988-05-12",
      "patientBirthSex": "Female",
      "patientAddress1": "2100 West Loop South",
      "patientCountry": "United States",
      "patientState": "Texas",
      "patientCity": "Houston",
      "patientZipcode": "77027",
      "patientAllergies": "CRESTOR, SULFA",
      "patientDiseases": "OSA, OA, NASH",
      "patientCellPhone": "+17135550155",
      "patientEmail": "patientemail@test.com",
      "deliveryAddress": "2100 West Loop South",
      "deliveryCity": "Houston",
      "deliveryState": "Texas",
      "deliveryZipCode": "77027",
      "deliveryPhone": "+17135550155",
      "shippingService": {
        "name": "Pick Up",
        "type": "pickup",
        "service_level": "Pick Up from Pharmacy",
        "state": "Texas",
        "price": "0",
        "id": "pickup",
        "weight": ">1",
        "carrier": "Pick Up"
      },
      "medication": [
        {
          "name": "Glutathione",
          "drugId": "152117761",
          "quantity": "1",
          "daysOfSupply": "60",
          "directions": [
                {
                    "month": 1,
                    "label": "1st month sig",
                    "text": "Inject 0.10ml (10 units) subq once weekly. (0.25mg)"
                },
                {
                    "month": 2,
                    "label": "2nd month sig",
                    "text": "Inject 0.10ml (10 units) subq once weekly. (0.25mg)"
                },
          ]
          "reasonForCompounding": "For health purposes"
        }
      ]
    }
  ]
}`;

const cancelOrderRequest = `{
  "orderId": "342524535"
}`

const createBulkOrderRequest = `{
  "doctorFirstname": "John",
  "doctorLastname": "Doe",
  "doctorPrefix": "Dr.",
  "doctorGender": "Male",
  "doctorAddress1": "123 Main St",
  "doctorNPI": "123456789X",
  "doctorDEA": "AB1234563",
  "doctorLicense": "MDCN/R/68971",
  "doctorHomePhone": "+13109180629",
  "doctorWorkPhone": "+17133609788",
  "doctorCountry": "United States",
  "doctorState": "Texas",
  "doctorCity": "Houston",
  "doctorZipcode": "77080",
  "doctorSignature": "Signed electronically",
  "doctorEmail": "doctoremail@test.com",
  "orderType": "New",
  "deliveryType": "Deliver to Doctor",
  "orderMethod": "Bulk",
  "orderBillingPreference": "Bill Facility",
  "patients": [
    {
      "patientFirstname": "Bulk",
      "patientLastname": "Placeholder",
      "patientDob": "1990-01-01",
      "patientBirthSex": "Female",
      "patientAddress1": "123 Main St",
      "patientCountry": "United States",
      "patientState": "Texas",
      "patientCity": "Houston",
      "patientZipcode": "77080",
      "patientAllergies": "CRESTOR, SULFA",
      "patientDiseases": "OSA, OA, NASH",
      "patientCellPhone": "+13109180629",
      "patientEmail": "patientemail@test.com",
      "deliveryAddress": "123 Main St",
      "deliveryCity": "Houston",
      "deliveryState": "Texas",
      "deliveryZipCode": "77080",
      "deliveryPhone": "+17133609788",
      "shippingService": {
        "name": "Pick Up",
        "type": "pickup",
        "service_level": "Pick Up from Pharmacy",
        "state": "Texas",
        "price": "0",
        "id": "pickup",
        "weight": ">1",
        "carrier": "Pick Up"
      },
      "medication": [
        {
          "name": "Glutathione",
          "drugId": "152117761",
          "quantity": "3",
          "direction": "Inject 0.10ml (10 units) subq once weekly. (0.25mg)"
        }
      ]
    }
  ]
}`;

const createOrderResponse = `{
  "message": "Orders created",
  "orderIds": ["477218095", "176493816", "259330629"]
}`;

const apiSpec = {
    title: "External API Documentation",
    description:
        "Use this API to fetch clinic-approved drugs and shipping services, then submit patient or bulk orders to BlendCareRx.",
    endpoints: [
        {
            method: "GET",
            title: "Get States",
            path: "/pharmacy/external/api/states",
            description:
                "Returns the supported states list used by the pharmacy integration.",
            params: [],
            notes: [
                "Use this endpoint when you need a reference list of supported states.",
            ],
            response: getStatesResponse,
        },
        {
            method: "GET",
            title: "Get Shipping Services",
            path: "/pharmacy/external/api/shipping_services?state={{state}}",
            description:
                "Returns shipping or pickup services available for the supplied state.",
            params: [{ name: "state", type: "string", required: true }],
            notes: [
                "Pass the returned shippingService object directly into each patients[i].shippingService value when creating an order.",
                "Pickup is always returned as an option when available for the facility.",
                "Send a real delivery state such as Texas or California in the query string.",
            ],
            response: getShippingServicesResponse,
        },
        {
            method: "GET",
            title: "Get Drugs",
            path: "/pharmacy/external/api/drugs",
            description:
                "Returns the drugs configured for the authenticated clinic along with pricing metadata used by the create-order flow.",
            params: [],
            notes: [
                "Use drugId when creating orders.",
                "For patient orders, select daysOfSupply from the returned dos array.",
                "Use the SIG endpoint if you want a ready-made list of direction values for medication.direction.",
                "For bulk orders, the API uses doctor_amount automatically.",
                "Do not send doctor_amount or patient_amount back in the create-order payload.",
            ],
            response: getDrugsResponse,
        },
        {
            method: "GET",
            title: "Get Directions (SIG)",
            path: "/pharmacy/external/api/get_sig",
            description:
                "Returns the available SIG/directions strings that can be used when creating medication items.",
            params: [],
            notes: [
                "Use one of the returned strings as the direction value on each medication item.",
                "You can also store these values locally in your clinic app for picker-style selection.",
            ],
            response: getSigResponse,
        },
        {
            method: "GET",
            title: "Get Reasons for Compounding",
            path: "/pharmacy/external/api/reasons",
            description:
                "Returns a list of compounding reason to choose from.",
            params: [],
            response: reasonsResponse,
        },
        {
            method: "GET",
            title: "Get Orders",
            path: "/pharmacy/external/api/orders",
            description:
                "Returns all orders submitted through the external API for the authenticated facility.",
            params: [],
            notes: [
                "Only orders created through the external API are returned.",
                "Medication and shipping_service are stored as JSON strings in the order payload.",
                "The response includes a steps array for Intake, PV1, Fulfillment, PV2, and Shipping.",
            ],
            response: getOrdersResponse,
        },
        {
            method: "GET",
            title: "Get Single Order",
            path: "/pharmacy/external/api/order/:orderId",
            description:
                "Returns one external API order for the authenticated facility.",
            params: [{ name: "orderId", type: "string", required: true }],
            notes: [
                "Returns null when the order does not belong to the authenticated facility.",
                "Medication and shipping_service are stored as JSON strings in the order payload.",
                "The response includes shipping and steps fields.",
            ],
            response: getSingleOrderResponse,
        },
         {
            method: "POST",
            title: "Cancel Order",
            path: "/pharmacy/external/api/order/cancel",
            description:
                "Cancels one external API order for the authenticated facility.",
            params: [],
            notes: [],
            requestBodies: [
                {
                    //title: "Cancel Order Example",
                    content: cancelOrderRequest,
                },
            ],
            response: cancelOrderResponse,
        },
         {
            method: "GET",
            title: "Download Certificate of Analysis (CoA)",
            path: "/pharmacy/external/api/order/coa/download/:orderId",
            description:
                "Downloads the CoA document tied to the order",
            params: [{ name: "orderId", type: "string", required: true }],
            notes: [
                "Returns Order not found when the order does not belong to the authenticated facility.",
                "Return CoA not available when document is not yet available",
            ],
            response: "",
        },
        {
            method: "POST",
            title: "Create Order",
            path: "/pharmacy/external/api/order",
            description:
                "Creates one order for each object inside the patients array.",
            params: [],
            notes: [
                "Accepted values for orderType: New, Refill.",
                "Accepted values for orderMethod: Patient, Bulk.",
                "Patient order pricing is resolved from the selected daysOfSupply value.",
                "Each medication item must include direction.",
                "Bulk order pricing is resolved from doctor_amount.",
                "Manual pricing fields are rejected.",
                "The response always returns orderIds as an array, even if only one order is created.",
                "daysOfSupply is required for patient orders.",
                "directions[array] is required when patients order."
            ],
            requestBodies: [
                {
                    title: "Patient Order Example",
                    content: createPatientOrderRequest,
                },
                {
                    title: "Bulk Order Example",
                    content: createBulkOrderRequest,
                },
            ],
            response: createOrderResponse,
        },
        {
            method: "POST",
            title: "Webhook Events",
            path: "YOUR_CONFIGURED_WEBHOOK_URL",
            description:
                "BlendCareRx sends webhook events to your configured HTTPS webhook URL when a VeRx order changes workflow or status.",
            params: [],
            notes: [
                "Webhooks are only sent for VeRx orders.",
                "A valid HTTPS webhook URL must be saved in clinic settings before delivery starts.",
                "A webhook secret must also be saved in clinic settings. Use the same value on your server when verifying signatures.",
                "Current status event names include: order.accepted, order.processed, order.dispensed, order.shipped, order.canceled, and order.rejected.",
                "Current workflow event names include: order.pv1_completed, order.fulfillment_updated, and order.pv2_completed.",
                "Webhook payloads include the full order snapshot plus doctor, pharmacy, facility, shipping, and steps.",
                "Webhook delivery is retried automatically when the receiving server fails or times out.",
                "The signature is created by combining the request timestamp, a period, and the exact raw JSON request body, then hashing that value with your webhook secret using HMAC-SHA256."
            ],
            response: webhookEventResponse,
        },
    ],
};

const methodColors = {
    GET: "bg-blue-600",
    POST: "bg-green-600",
    PUT: "bg-yellow-600",
    DELETE: "bg-red-600",
};

export default function ApiDocPage() {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [modalTitle, setModalTitle] = useState("");

    const openModal = (title, content) => {
        setModalTitle(title);
        setModalContent(content);
        setModalOpen(true);
    };

    return (
        <div className="flex w-full min-h-screen bg-gray-50">
            <aside className="w-[25%] bg-white border-r p-5 h-[75vh] sticky top-0 overflow-hidden">
                <h2 className="text-xl font-bold mb-4">Documentation</h2>
                <a href="#overview" className="block mb-2 text-primary hover:underline">
                    Overview
                </a>
                <a href="#field-reference" className="block mb-2 text-primary hover:underline">
                    Field Reference
                </a>
                {apiSpec.endpoints.map((endpoint, index) => (
                    <a
                        key={endpoint.title}
                        href={`#endpoint-${index}`}
                        className="block mb-2 text-primary hover:underline"
                    >
                        {endpoint.title}
                    </a>
                ))}
            </aside>

            <div className="flex-1 px-10 w-[75%] py-8">
                <h1 className="text-3xl font-bold mb-3">{apiSpec.title}</h1>
                <p className="text-gray-700 mb-8">{apiSpec.description}</p>

                <div id="overview" className="mb-10 p-6 bg-white border rounded-lg shadow-sm">
                    <h2 className="text-2xl font-bold mb-4">Overview</h2>
                    <p className="text-gray-700 mb-3">
                        The Pharmacy External API allows your clinic application to fetch supported
                        states, shipping services, and configured drugs, then submit patient or bulk
                        orders to the pharmacy.
                    </p>
                    <p className="text-gray-700 mb-3">
                        All requests must include your API key in the Authorization header:
                    </p>
                    <pre className="bg-black text-green-400 p-4 rounded text-sm overflow-auto mb-4">
                        Authorization: Bearer &lt;YOUR_API_KEY&gt;
                    </pre>
                    <p className="text-gray-700 mb-3">
                        Base URL: <code>https://bcrx-api.careapps.net</code>
                    </p>

                    <h3 className="text-xl font-semibold mt-6 mb-3">Recommended Integration Flow</h3>
                    <ul className="list-disc ml-6 text-gray-700 space-y-2">
                        {integrationSteps.map((step) => (
                            <li key={step}>{step}</li>
                        ))}
                    </ul>

                    <h3 className="text-xl font-semibold mt-6 mb-3">Pricing and Validation Rules</h3>
                    <ul className="list-disc ml-6 text-gray-700 space-y-2">
                        {orderRules.map((rule) => (
                            <li key={rule}>{rule}</li>
                        ))}
                    </ul>

                    <h3 className="text-xl font-semibold mt-6 mb-3">Webhook Security</h3>
                    <p className="text-gray-700 mb-3">
                        BlendCareRx signs every webhook request. Save a shared webhook secret in clinic settings and verify the request before processing it.
                    </p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-2 mb-4">
                        <li>Choose a secret value in your clinic settings and save it in the Webhook Secret field.</li>
                        <li>Store that same value on your server, for example as <code>BLENDCARE_WEBHOOK_SECRET</code>.</li>
                        <li>Do not expose the secret in frontend code or client-side JavaScript.</li>
                        <li>When your webhook endpoint receives a request, read <code>X-BlendCare-Timestamp</code>, then get the exact raw JSON body exactly as it was received.</li>
                        <li>Build this exact string before hashing: <code>{`timestamp.rawBody`}</code> where the period is a literal <code>.</code> between the timestamp and the raw JSON body.</li>
                        <li>Use your saved secret to compute an HMAC-SHA256 hash of that string.</li>
                        <li>Prefix the hex digest with <code>sha256=</code> and compare the final value with <code>X-BlendCare-Signature</code>.</li>
                        <li>Only process the webhook when the computed signature matches.</li>
                    </ul>
                    <div className="mb-4">
                        <div className="text-sm font-medium mb-2">Headers</div>
                        <CodePreview
                            content={webhookHeadersExample}
                            onShowMore={() => openModal("Webhook Headers", webhookHeadersExample)}
                        />
                    </div>
                    <div>
                        <div className="text-sm font-medium mb-2">Node.js Verification Example</div>
                        <FullCodeBlock content={webhookVerificationExample} />
                    </div>
                    <div className="mt-4">
                        <div className="text-sm font-medium mb-2">Python Verification Example</div>
                        <FullCodeBlock content={webhookVerificationPythonExample} />
                    </div>
                </div>

                {apiSpec.endpoints.map((endpoint, index) => (
                    <div
                        id={`endpoint-${index}`}
                        key={endpoint.title}
                        className="mb-10 p-6 bg-white border rounded-lg shadow-sm"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <span
                                className={`text-white text-sm px-3 py-1 rounded ${methodColors[endpoint.method]}`}
                            >
                                {endpoint.method}
                            </span>
                            <code className="text-lg font-mono">{endpoint.path}</code>
                        </div>

                        <p className="text-gray-700 mb-4">{endpoint.description}</p>

                        {endpoint.notes?.length > 0 && (
                            <div className="mb-4">
                                <h3 className="font-semibold mb-2">Notes</h3>
                                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                                    {endpoint.notes.map((note) => (
                                        <li key={note}>{note}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {endpoint.params.length > 0 && (
                            <div className="mb-4">
                                <h3 className="font-semibold mb-2">Parameters</h3>
                                <ul className="list-disc ml-6">
                                    {endpoint.params.map((param) => (
                                        <li key={param.name}>
                                            <strong>{param.name}</strong> ({param.type})
                                            {param.required ? (
                                                <span className="text-red-600"> *required</span>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {endpoint.requestBodies?.length > 0 && (
                            <div className="mb-6">
                                <h3 className="font-semibold mb-2">Request Body Examples</h3>
                                <div className="space-y-4">
                                    {endpoint.requestBodies.map((requestBody) => (
                                        <div key={requestBody.title}>
                                            <div className="text-sm font-medium mb-2">
                                                {requestBody.title}
                                            </div>
                                            <CodePreview
                                                content={requestBody.content}
                                                onShowMore={() =>
                                                    openModal(requestBody.title, requestBody.content)
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mb-4">
                            <h3 className="font-semibold mb-2">Response</h3>
                            <CodePreview
                                content={endpoint.response}
                                onShowMore={() =>
                                    openModal(`${endpoint.title} Response`, endpoint.response)
                                }
                            />
                        </div>

                    </div>
                ))}

                <div id="field-reference" className="mb-10 p-6 bg-white border rounded-lg shadow-sm">
                    <h2 className="text-2xl font-bold mb-4">Field Reference</h2>
                    <p className="text-gray-700 mb-6">
                        Use the tables below when building the request body for the create-order endpoint.
                    </p>

                    <FieldTable
                        title="Top-Level Create Order Fields"
                        subtitle="Fields sent once per request"
                        fields={topLevelOrderFields}
                    />

                    <FieldTable
                        title="patients[i] Object Fields"
                        subtitle="Fields sent for each order you want to create"
                        fields={patientObjectFields}
                    />

                    <FieldTable
                        title="Patient Medication Fields"
                        subtitle="Use this schema when orderMethod is Patient"
                        fields={patientMedicationFields}
                    />

                    <FieldTable
                        title="Bulk Medication Fields"
                        subtitle="Use this schema when orderMethod is Bulk"
                        fields={bulkMedicationFields}
                    />

                    <FieldTable
                        title="Shipping Service Object Fields"
                        subtitle="Use an object returned from the shipping services endpoint"
                        fields={shippingServiceFields}
                    />
                </div>
            </div>

            <ResponseModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={modalTitle}
                content={modalContent}
            />
        </div>
    );
}

function CodePreview({ content, onShowMore }) {
    return (
        <div>
            <div className="relative max-h-52 overflow-hidden max-w-full rounded border">
                <pre className="bg-black text-green-400 p-4 text-sm overflow-auto">
                    {content}
                </pre>
                <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-black/80 to-transparent"></div>
            </div>
            {content.trim() !== "" ? (
                <button onClick={onShowMore} className="mt-2 text-primary hover:underline">
                    Show more
                </button>
            ) : null}
        </div>
    );
}

CodePreview.propTypes = {
    content: PropTypes.string.isRequired,
    onShowMore: PropTypes.func.isRequired,
};

function FullCodeBlock({ content }) {
    return (
        <div>
            <div className="max-w-full rounded border">
                <pre className="overflow-auto bg-black p-4 text-sm text-green-400">
                    {content}
                </pre>
            </div>
        </div>
    );
}

FullCodeBlock.propTypes = {
    content: PropTypes.string.isRequired,
};

function FieldTable({ title, subtitle, fields }) {
    return (
        <div className="mb-6">
            <h3 className="font-semibold mb-2">
                {title}{" "}
                {subtitle ? (
                    <span className="text-[14px] font-normal text-gray-600">({subtitle})</span>
                ) : null}
            </h3>
            <table className="table-auto border-collapse border border-gray-300 w-full text-sm">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 px-3 py-2 text-left">Field</th>
                        <th className="border border-gray-300 px-3 py-2 text-left">Type</th>
                        <th className="border border-gray-300 px-3 py-2 text-left">Required</th>
                        <th className="border border-gray-300 px-3 py-2 text-left">Notes</th>
                    </tr>
                </thead>
                <tbody>
                    {fields.map((field) => (
                        <tr key={field.name}>
                            <td className="border border-gray-300 px-3 py-2">{field.name}</td>
                            <td className="border border-gray-300 px-3 py-2">{field.type}</td>
                            <td className="border border-gray-300 px-3 py-2">
                                {field.required ? "Yes" : "No"}
                            </td>
                            <td className="border border-gray-300 px-3 py-2">{field.note}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

FieldTable.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            required: PropTypes.bool.isRequired,
            note: PropTypes.string.isRequired,
        })
    ).isRequired,
};

function ResponseModal({ open, onClose, title, content }) {
    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white w-[80%] max-h-[80vh] rounded shadow-lg overflow-auto relative scrollbar-hide">
                <div className="flex justify-between items-center px-6 py-3 sticky top-0 left-0 right-0 bg-white border-b">
                    <h2 className="text-[16px] font-medium">{title || "Full Response"}</h2>
                    <button onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <pre className="bg-black text-green-400 p-4 text-sm overflow-auto scrollbar-hide">
                    {content}
                </pre>
            </div>
        </div>
    );
}

ResponseModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    content: PropTypes.string.isRequired,
};
